import { useMemo } from 'react';
import { escapeRegExp } from 'lodash';

/**
 * A generic filtering and mapping hook which takes an array of items and maps items
 * to { label, value } format for dropdowns
 *
 * Filters items based on search string specified by user using a regex matching expression
 * Supports reusability; used to support search queries for product groups. categories,
 * extraDimensions, and subcategories
 */

interface Option {
  label: string;
  value: string;
}

const useFilteredOptions = <T>(
  items: T[],
  filter: string,
  mapToOption: (item: T) => Option,
  searchField?: (item: T) => string,
) =>
  useMemo(() => {
    if (!filter) return items.map(mapToOption);

    // filters data presented by matching regex from beginning of list of results
    const filterRegExp = new RegExp(escapeRegExp(filter), 'i');
    return items
      .filter((item) => {
        const searchValue = searchField ? searchField(item) : mapToOption(item).label;
        return filterRegExp.test(searchValue);
      })
      .map(mapToOption);
  }, [items, filter, mapToOption, searchField]);

export default useFilteredOptions;
